import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FiCopy, FiRefreshCw, FiSave } from 'react-icons/fi';
import toast from 'react-hot-toast';

import * as api from '../api/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { AuthContext } from '../context/AuthContext';

const n = (value, digits=2) => value == null ? '—' : Number(value).toFixed(digits);

const Plans = () => {
  const { isLocalExecution } = useContext(AuthContext);
  const [params] = useSearchParams();
  const queryTicker = (params.get('ticker') || '').toUpperCase();
  const requestedSetup = (params.get('setup') || '').toUpperCase();
  const requestedPlanId = params.get('plan') || '';
  const [loading,setLoading] = useState(true);
  const [latest,setLatest] = useState(null);
  const [settings,setSettings] = useState({});
  const [riskCapacity,setRiskCapacity] = useState(null);
  const [plans,setPlans] = useState([]);
  const [preview,setPreview] = useState(null);
  const [saved,setSaved] = useState(null);
  const [executingPlanId,setExecutingPlanId] = useState(null);
  const [reviewingPlanId,setReviewingPlanId] = useState(null);
  const [externalPlanId,setExternalPlanId] = useState(null);
  const [externalSaving,setExternalSaving] = useState(false);
  const [externalForm,setExternalForm] = useState({entryPrice:'',exitPrice:'',quantity:'',entryFee:'',exitFee:'',protectiveStop:'',entryTime:'',exitTime:'',brokerLabel:'',notes:''});
  const [form,setForm] = useState({
    symbol: queryTicker,
    disposition: 'PLANNED',
    setupFamily: 'MANUAL_STRUCTURAL',
    setupVariant: '',
    entryMethod: 'MANUAL_LEVELS',
    trigger: '',
    maxEntry: '',
    stop: '',
    expiry: '',
    invalidation: '',
    managementRule: 'Manual management; no stop widening.',
    decisionReason: '',
    notes: '',
    equity: '',
    riskBudget: '',
    maxPositionPercent: '',
    costReserve: '0',
    gapFallbackOrb5: true,
  });

  const refresh = useCallback(async () => {
    const [intel,global,risk,planRows] = await Promise.all([
      api.getIntelligenceLatest().catch(()=>({data:null})),
      api.getGlobalSettings().catch(()=>({data:{}})),
      api.getRiskCapacity().catch(()=>({data:null})),
      api.getTradePlans(queryTicker || null).catch(()=>({data:[]})),
    ]);
    setLatest(intel.data);
    setSettings(global.data || {});
    setRiskCapacity(risk.data || null);
    setPlans(planRows.data || []);
    setLoading(false);
  }, [queryTicker]);

  useEffect(()=>{ refresh(); }, [refresh]);

  useEffect(()=>{
    const equity = Number(settings.portfolio_value || 0);
    const configured = Number(settings.risk_value || 0);
    const riskBudget = settings.risk_model === 'percentage' ? equity * configured / 100 : configured;
    setForm(current => ({
      ...current,
      equity: current.equity || (equity ? String(equity) : ''),
      riskBudget: current.riskBudget || (riskBudget ? String(riskBudget) : ''),
      maxPositionPercent: current.maxPositionPercent || (settings.max_pos_percent ? String(settings.max_pos_percent) : ''),
      costReserve: current.costReserve === '0' && settings.cost_reserve ? String(settings.cost_reserve) : current.costReserve,
    }));
  }, [settings]);

  const leaders = latest?.daily?.scan?.leaders || [];
  const opening = latest?.opening?.opening?.stocks_in_play || [];
  const themes = useMemo(()=>Object.values(latest?.themes?.themes || {}),[latest]);
  const selectedLeader = leaders.find(row=>row.symbol === form.symbol) || null;
  const selectedOpening = opening.find(row=>row.symbol === form.symbol) || null;
  const selectedTheme = themes.find(t=>(t.tickers || []).includes(form.symbol)) || null;
  const regime = latest?.daily?.regime || null;
  const ipo01Candidate = selectedLeader?.setup_candidates?.IPO01 || null;
  const pb01Candidate = selectedLeader?.setup_candidates?.PB01 || null;
  const pb02Candidate = selectedLeader?.setup_candidates?.PB02 || null;
  const selectedAlgoDetail = selectedOpening?.algostock_score_detail || selectedLeader?.algostock_score_detail || null;

  useEffect(()=>{
    const candidate = requestedSetup === 'IPO01'
      ? ipo01Candidate
      : requestedSetup === 'PB01'
        ? pb01Candidate
        : requestedSetup === 'PB02' ? pb02Candidate : null;
    if (!candidate || candidate.state !== 'STRUCTURE_CANDIDATE') return;
    setForm(current => {
      if (current.setupFamily === requestedSetup && current.setupVariant === candidate.variant) return current;
      const structure = candidate.structure || {};
      const stopReference = structure.structural_low_reference;
      return {
        ...current,
        setupFamily: requestedSetup,
        setupVariant: candidate.variant || '',
        entryMethod: 'BREAKOUT',
        invalidation: current.invalidation || (
          stopReference != null
            ? `${requestedSetup} research structure fails below the frozen structural level chosen from reference ${stopReference}.`
            : `Define the exact structural invalidation before freezing this ${requestedSetup} plan.`
        ),
      };
    });
  }, [requestedSetup, ipo01Candidate, pb01Candidate, pb02Candidate]);

  const set = (key) => (event) => {
    setPreview(null);
    setSaved(null);
    setForm(current=>({...current,[key]:event.target.value}));
  };

  const riskPolicy = () => ({
    equity: Number(form.equity),
    risk_budget: Number(form.riskBudget),
    max_position_percent: Number(form.maxPositionPercent),
    cost_reserve: Number(form.costReserve || 0),
    base_currency: settings.base_currency || 'USD',
    policy_id: settings.risk_policy_id || 'manual-plan-v1',
  });

  const positive = (value) => {
    if (String(value ?? '').trim() === '') return null;
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? number : null;
  };

  const planIssues = (() => {
    const issues = [];
    if (!form.symbol.trim()) issues.push('Ticker is required.');
    if (form.disposition !== 'PLANNED') {
      if (!form.decisionReason.trim()) issues.push('Decision reason is required.');
      return issues;
    }

    const trigger = positive(form.trigger);
    const maxEntry = positive(form.maxEntry);
    const stop = positive(form.stop);
    const equity = positive(form.equity);
    const riskBudget = positive(form.riskBudget);
    const maxPosition = positive(form.maxPositionPercent);
    const reserve = String(form.costReserve ?? '').trim() === '' ? 0 : Number(form.costReserve);

    if (!form.setupFamily) issues.push('Setup family is required.');
    if (!form.entryMethod) issues.push('Entry method is required.');
    if (!trigger) issues.push('Trigger must be greater than zero.');
    if (!maxEntry) issues.push('Max entry must be greater than zero.');
    if (!stop) issues.push('Initial stop must be greater than zero.');
    if (stop && trigger && stop >= trigger) issues.push('Initial stop must be below trigger.');
    if (trigger && maxEntry && trigger > maxEntry) issues.push('Trigger cannot exceed max entry.');
    const expiryTime = form.expiry ? new Date(form.expiry).getTime() : NaN;
    if (!Number.isFinite(expiryTime)) issues.push('A valid expiry is required.');
    else if (expiryTime <= Date.now()) issues.push('Expiry must be in the future.');
    if (!form.invalidation.trim()) issues.push('Structural invalidation is required.');
    if (!form.managementRule.trim()) issues.push('Management rule is required.');
    if (!equity) issues.push('Account equity must be greater than zero.');
    if (!riskBudget) issues.push('Risk budget must be greater than zero.');
    if (!maxPosition || maxPosition > 100) issues.push('Max position must be between 0 and 100%.');
    if (!Number.isFinite(reserve) || reserve < 0) issues.push('Cost reserve cannot be negative.');
    return issues;
  })();

  const riskPreviewReady = (() => {
    const maxEntry = positive(form.maxEntry);
    const stop = positive(form.stop);
    const equity = positive(form.equity);
    const riskBudget = positive(form.riskBudget);
    const maxPosition = positive(form.maxPositionPercent);
    const reserve = String(form.costReserve ?? '').trim() === '' ? 0 : Number(form.costReserve);
    return Boolean(
      maxEntry && stop && stop < maxEntry && equity && riskBudget &&
      maxPosition && maxPosition <= 100 && Number.isFinite(reserve) && reserve >= 0
    );
  })();

  const previewRisk = async () => {
    try {
      const response = await api.previewPlanRisk({
        max_entry_price: Number(form.maxEntry),
        stop_price: Number(form.stop),
        risk_policy: riskPolicy(),
      });
      setPreview(response.data);
    } catch (error) {
      setPreview(null);
      toast.error(error.response?.data?.detail || 'Risk preview failed.');
    }
  };

  const planPayload = () => {
    const base = {
      symbol: form.symbol.trim().toUpperCase(),
      disposition: form.disposition,
      as_of: new Date().toISOString(),
      decision_reason: form.decisionReason || null,
      notes: form.notes || null,
    };
    if (form.disposition !== 'PLANNED') return base;
    return {
      ...base,
      session_date: latest?.daily?.session_date || null,
      setup_family: form.setupFamily,
      setup_variant: form.setupVariant || null,
      entry_method: form.entryMethod,
      trigger_price: Number(form.trigger),
      max_entry_price: Number(form.maxEntry),
      stop_price: Number(form.stop),
      expiry: new Date(form.expiry).toISOString(),
      structural_invalidation: form.invalidation,
      source_snapshot: {
        source: 'PLAN_BUILDER',
        captured_at: new Date().toISOString(),
        intelligence_available: Boolean(latest),
        synthetic_data: Boolean(latest?.daily?.synthetic_data),
      },
      chain_snapshot: {
        market: regime,
        theme: selectedTheme,
      },
      leader_snapshot: selectedLeader || { status: 'NOT_IN_CURRENT_LEADER_SET' },
      structure_snapshot: {
        manual_levels: true,
        opening_observation: selectedOpening,
        research_candidate: form.setupFamily === 'IPO01'
          ? ipo01Candidate
          : form.setupFamily === 'PB01'
            ? pb01Candidate
            : form.setupFamily === 'PB02' ? pb02Candidate : null,
        gap_fallback: {
          enabled: Boolean(form.gapFallbackOrb5),
          mode: 'ORB_5',
          range_minutes: 5,
          trigger_offset_rule: '0.01 below $100, 0.10 at/above $100',
          limit_buffer_pct: 0.005,
          stop_basis: 'ORB_LOW_MINUS_TICK_OFFSET',
          requires_original_daily_trigger_inside_or_below_orb_high: true,
        },
      },
      event_context: {
        status: 'UNVERIFIED',
        user_confirmation_required_before_arming: true,
        earnings: selectedLeader?.fundamental_profile?.next_earnings || null,
        latest_reported_earnings: selectedLeader?.fundamental_profile?.earnings || null,
        source: selectedLeader?.fundamental_profile?.source || null,
      },
      management_policy: {
        mode: 'MANUAL',
        rule: form.managementRule,
        partials: [],
        stop_widening: false,
      },
      risk_policy: riskPolicy(),
    };
  };

  const savePlan = async () => {
    if (planIssues.length) {
      toast.error(planIssues[0]);
      return;
    }
    try {
      const response = await api.createTradePlan(planPayload());
      setSaved(response.data);
      setPreview(response.data.sizing_snapshot || null);
      toast.success(response.data.disposition === 'PLANNED' ? 'Plan frozen.' : 'Decision logged.');
      const rows = await api.getTradePlans(form.symbol || null);
      setPlans(rows.data || []);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save plan.');
    }
  };

  const executePaper = async (plan) => {
    const confirmed = window.confirm(
      `Submit ${plan.symbol} v${plan.version} to the configured IBKR PAPER account?\n\n` +
      'This uses the frozen trigger, max-entry and stop. It may create a real PAPER broker order, never a live order.'
    );
    if (!confirmed) return;
    setExecutingPlanId(plan.id);
    try {
      const response = await api.executeTradePlanPaper(plan.id, true);
      if (response.data.waiting_for_orb) {
        toast.success('Gap-through: manual ticket skipped. Waiting for the completed 5-minute ORB.');
      } else {
        toast.success(`Paper order submitted: ${response.data.order?.id || 'accepted'}`);
      }
      const rows = await api.getTradePlans(form.symbol || null);
      setPlans(rows.data || []);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Paper execution was blocked safely.');
    } finally {
      setExecutingPlanId(null);
    }
  };

  const openExternalClose = (plan) => {
    setExternalPlanId(current => current === plan.id ? null : plan.id);
    setExternalForm({
      entryPrice:'', exitPrice:'',
      quantity:plan.sizing_snapshot?.final_quantity ? String(plan.sizing_snapshot.final_quantity) : '',
      entryFee:'', exitFee:'',
      protectiveStop:plan.stop_price != null ? String(plan.stop_price) : '',
      entryTime:'', exitTime:'', brokerLabel:'', notes:'',
    });
  };

  const setExternal = (key) => (event) => setExternalForm(current=>({...current,[key]:event.target.value}));

  const submitExternalClose = async (plan) => {
    if (!externalForm.entryPrice || !externalForm.exitPrice || !externalForm.quantity || !externalForm.entryTime || !externalForm.exitTime) {
      toast.error('Entry, exit, quantity and both timestamps are required.');
      return;
    }
    setExternalSaving(true);
    try {
      const quantity = Number(externalForm.quantity);
      const response = await api.recordExternalTradeClose(plan.id, {
        broker_label: externalForm.brokerLabel || null,
        currency: 'USD',
        protective_stop_price: externalForm.protectiveStop ? Number(externalForm.protectiveStop) : null,
        notes: externalForm.notes || null,
        fills: [
          {side:'buy', quantity, price:Number(externalForm.entryPrice), fee:externalForm.entryFee === '' ? null : Number(externalForm.entryFee), time:new Date(externalForm.entryTime).toISOString()},
          {side:'sell', quantity, price:Number(externalForm.exitPrice), fee:externalForm.exitFee === '' ? null : Number(externalForm.exitFee), time:new Date(externalForm.exitTime).toISOString()},
        ],
      });
      const trade = response.data.trade;
      toast.success(trade.net_pnl == null ? 'External trade recorded; net P/L awaits complete fees.' : `External trade recorded: ${n(trade.net_pnl)} net · ${n(trade.r_multiple)}R`);
      setExternalPlanId(null);
      const rows = await api.getTradePlans(form.symbol || null);
      setPlans(rows.data || []);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not record external trade.');
    } finally {
      setExternalSaving(false);
    }
  };

  const reviewClosedPlan = async (plan) => {
    const note = window.prompt('Review note: what happened versus the frozen plan?');
    if (note === null) return;
    setReviewingPlanId(plan.id);
    try {
      await api.addTradePlanEvent(plan.id, 'REVIEWED', { note: note.trim() || null });
      toast.success('Plan reviewed and lifecycle closed.');
      const rows = await api.getTradePlans(form.symbol || null);
      setPlans(rows.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not close the review.');
    } finally {
      setReviewingPlanId(null);
    }
  };

  const copyExport = async (planId) => {
    try {
      const response = await api.exportTradePlan(planId);
      await navigator.clipboard.writeText(response.data.text);
      toast.success('Plan copied for external execution.');
      const rows = await api.getTradePlans(form.symbol || null);
      setPlans(rows.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not export plan.');
    }
  };

  if (loading) return <div className="workspace-page"><LoadingSpinner/></div>;

  return (
    <div className="workspace-page terminal-page">
      <div className="command-header">
        <div>
          <div className="eyebrow">{isLocalExecution ? 'Decision → risk → external execution' : 'Decision → risk → trade plan'}</div>
          <h1 className="command-title">Trade Plans</h1>
          <div className="command-subtitle">{isLocalExecution ? 'Freeze the idea before execution. Saving a plan never sends or reserves a broker order.' : 'Record your setup, entry, stop and risk before making a trading decision.'}</div>
        </div>
        <button className="ws-button" onClick={refresh}><FiRefreshCw/> Refresh</button>
      </div>

      <div className="ws-banner warning">
        <strong>Broker-independent.</strong>
        Risk is a planning estimate until account coverage, event context and execution state are verified. Stop fills are not guaranteed.
      </div>

      {riskCapacity?.configured ? (
        <div className="plan-context-strip" aria-label="Portfolio risk ledger">
          <div><span>Reserved risk</span><strong>{n(riskCapacity.reserved_risk)}</strong></div>
          <div><span>Open risk</span><strong>{n(riskCapacity.open_risk)}</strong></div>
          <div><span>Used risk</span><strong>{n(riskCapacity.used_risk)}</strong></div>
          <div><span>Available risk</span><strong>{n(riskCapacity.available_risk)}</strong></div>
          <div><span>Capital used</span><strong>{n(riskCapacity.capital_used)}</strong></div>
          <div><span>Coverage</span><strong>ALGOSTOCK-MANAGED</strong></div>
        </div>
      ) : (
        <div className="ws-banner warning" role="status">
          <strong>{isLocalExecution ? 'Portfolio risk ledger not configured.' : 'Single-plan risk estimate.'}</strong>
          <span>{isLocalExecution ? 'Saved quantities remain single-plan estimates until account scope and total risk capacity are configured.' : 'Review your total portfolio exposure before using any saved quantity.'}</span>
        </div>
      )}

      {riskCapacity?.scope && (
        <div className="section-meta">Risk scope: {riskCapacity.scope}</div>
      )}

      <div className="plan-layout">
        <section className="ws-panel pad plan-builder">
          <div className="section-header">
            <h2 className="section-title">New immutable decision</h2>
            <span className="section-meta">{selectedLeader ? 'Current leader context found' : 'Manual context'}</span>
          </div>

          <div className="plan-form-grid">
            <label><span>Ticker</span><input className="ws-input" value={form.symbol} onChange={set('symbol')} placeholder="Ticker symbol"/></label>
            <label><span>Decision</span>
              <select className="ws-input" value={form.disposition} onChange={set('disposition')}>
                <option value="PLANNED">PLANNED</option>
                <option value="NO_TRADE">NO TRADE</option>
                <option value="REJECTED">REJECTED</option>
              </select>
            </label>
          </div>

          {form.disposition !== 'PLANNED' ? (
            <label className="plan-full"><span>Decision reason</span>
              <textarea className="ws-input plan-textarea" value={form.decisionReason} onChange={set('decisionReason')} placeholder="Why is this not a valid trade?"/>
            </label>
          ) : (
            <>
              <div className="plan-context-strip">
                <div><span>Market</span><strong>{regime?.label || 'UNKNOWN'}</strong></div>
                <div><span>Theme</span><strong>{selectedTheme?.theme || 'STANDALONE / UNKNOWN'}</strong></div>
                <div><span>AlgoStock Score</span><strong>{selectedOpening ? n(selectedOpening.algostock_score,1) : selectedLeader ? n(selectedLeader.algostock_score,1) : 'UNVERIFIED'}</strong></div>
                <div><span>Tier</span><strong>{selectedAlgoDetail?.tier || '—'}</strong></div>
                <div><span>Evidence</span><strong>{selectedAlgoDetail?.evidence_state || '—'}</strong></div>
                <div><span>Coverage</span><strong>{selectedAlgoDetail?.coverage == null ? '—' : Math.round(Number(selectedAlgoDetail.coverage) * 100) + '%'}</strong></div>
                <div><span>Opening</span><strong>{selectedOpening ? (n(selectedOpening.rvol5,2) + 'x RVOL') : 'NO CONFIRMATION'}</strong></div>
              </div>

              {ipo01Candidate && (
                <div className="plan-context-strip">
                  <div><span>IPO01 state</span><strong>{ipo01Candidate.state || '—'}</strong></div>
                  <div><span>Sessions</span><strong>{ipo01Candidate.structure?.listing_sessions ?? '—'}</strong></div>
                  <div><span>Base depth</span><strong>{ipo01Candidate.structure?.base_depth == null ? '—' : (Number(ipo01Candidate.structure.base_depth) * 100).toFixed(1) + '%'}</strong></div>
                  <div><span>Trigger ref.</span><strong>{n(ipo01Candidate.structure?.trigger_reference)}</strong></div>
                  <div><span>Structural low</span><strong>{n(ipo01Candidate.structure?.structural_low_reference)}</strong></div>
                  <div><span>Setup quality</span><strong>{n(ipo01Candidate.setup_quality_score,0)}</strong></div>
                </div>
              )}
              {pb01Candidate && (
                <div className="plan-context-strip">
                  <div><span>PB01 state</span><strong>{pb01Candidate.state || '—'}</strong></div>
                  <div><span>Research</span><strong>{pb01Candidate.research_status || '—'}</strong></div>
                  <div><span>Trigger ref.</span><strong>{n(pb01Candidate.structure?.trigger_reference)}</strong></div>
                  <div><span>Structural low ref.</span><strong>{n(pb01Candidate.structure?.structural_low_reference)}</strong></div>
                  <div><span>VCP evidence</span><strong>{pb01Candidate.vcp_evidence?.progressive_range_contraction && pb01Candidate.vcp_evidence?.progressive_volume_contraction ? 'RANGE + VOLUME' : 'PARTIAL / NO'}</strong></div>
                  <div><span>Tag</span><strong>{(pb01Candidate.context_tags || []).join(', ') || '—'}</strong></div>
                </div>
              )}
              {pb02Candidate && (
                <div className="plan-context-strip">
                  <div><span>PB02 state</span><strong>{pb02Candidate.state || '—'}</strong></div>
                  <div><span>Research</span><strong>{pb02Candidate.research_status || '—'}</strong></div>
                  <div><span>Trigger ref.</span><strong>{n(pb02Candidate.structure?.trigger_reference)}</strong></div>
                  <div><span>Structural low ref.</span><strong>{n(pb02Candidate.structure?.structural_low_reference)}</strong></div>
                </div>
              )}

              <div className="plan-form-grid">
                <label><span>Setup family</span>
                  <select className="ws-input" value={form.setupFamily} onChange={set('setupFamily')}>
                    <option value="MANUAL_STRUCTURAL">Manual structural plan</option>
                    <option value="IPO01">IPO01 base candidate — research hypothesis</option>
                    <option value="PB01">PB01 candidate — research hypothesis</option>
                    <option value="PB02">PB02 pullback candidate — research hypothesis</option>
                  </select>
                </label>
                <label><span>Variant</span><input className="ws-input" value={form.setupVariant} onChange={set('setupVariant')} placeholder="Optional version label"/></label>
                <label><span>Entry method</span>
                  <select className="ws-input" value={form.entryMethod} onChange={set('entryMethod')}>
                    <option value="MANUAL_LEVELS">Manual structural levels</option>
                    <option value="BREAKOUT">Breakout trigger</option>
                  </select>
                </label>
                <label><span>Expiry</span><input className="ws-input" type="datetime-local" value={form.expiry} onChange={set('expiry')}/></label>
              </div>

              <div className="plan-levels">
                <label><span>Trigger</span><input className="ws-input" inputMode="decimal" value={form.trigger} onChange={set('trigger')} placeholder="Trigger price"/></label>
                <label><span>Max entry</span><input className="ws-input" inputMode="decimal" value={form.maxEntry} onChange={set('maxEntry')} placeholder="Max entry price"/></label>
                <label><span>Initial stop</span><input className="ws-input" inputMode="decimal" value={form.stop} onChange={set('stop')} placeholder="Initial stop price"/></label>
              </div>

              <label className="plan-full" style={{display:'flex',gap:'.65rem',alignItems:'flex-start'}}>
                <input
                  type="checkbox"
                  checked={Boolean(form.gapFallbackOrb5)}
                  onChange={event => {
                    setPreview(null);
                    setSaved(null);
                    setForm(current => ({...current, gapFallbackOrb5:event.target.checked}));
                  }}
                  style={{marginTop:'.25rem'}}
                />
                <span>
                  <strong>Gap-through fallback: 5-minute ORB</strong><br/>
                  If price opens/trades above the frozen max entry before fill, cancel the manual ticket and wait for a new 5m ORB plan with fresh trigger, stop and sizing.
                </span>
              </label>

              <label className="plan-full"><span>Structural invalidation</span>
                <textarea className="ws-input plan-textarea" value={form.invalidation} onChange={set('invalidation')} placeholder="What specifically breaks the trade idea?"/>
              </label>

              <div className="plan-risk-grid">
                <label><span>Account equity</span><input className="ws-input" inputMode="decimal" value={form.equity} onChange={set('equity')}/></label>
                <label><span>Risk budget</span><input className="ws-input" inputMode="decimal" value={form.riskBudget} onChange={set('riskBudget')}/></label>
                <label><span>Max position %</span><input className="ws-input" inputMode="decimal" value={form.maxPositionPercent} onChange={set('maxPositionPercent')}/></label>
                <label><span>Cost reserve</span><input className="ws-input" inputMode="decimal" value={form.costReserve} onChange={set('costReserve')}/></label>
              </div>

              <label className="plan-full"><span>Management policy</span>
                <textarea className="ws-input plan-textarea" value={form.managementRule} onChange={set('managementRule')}/>
              </label>

              <div className="plan-actions">
                <button className="ws-button" onClick={previewRisk} disabled={!riskPreviewReady}>Preview risk</button>
                <span>No portfolio reservation is created by preview or save.</span>
              </div>

              {preview && (
                <>
                  <div className="plan-risk-preview">
                    <div><span>Quantity</span><strong>{preview.final_quantity}</strong></div>
                    <div><span>Risk / share</span><strong>{n(preview.risk_per_share)}</strong></div>
                    <div><span>Planned max loss</span><strong>{n(preview.planned_maximum_loss)}</strong></div>
                    <div><span>Notional</span><strong>{n(preview.capital_used)}</strong></div>
                  </div>
                  <div className={`ws-banner ${preview.portfolio_capacity_verified ? '' : 'warning'}`}>
                    <strong>{preview.portfolio_capacity_verified ? 'Portfolio capacity provided.' : 'Single-plan sizing only.'}</strong>
                    <span>{preview.portfolio_capacity_verified
                      ? 'The preview used the supplied available capital and risk. Saving still creates no broker reservation.'
                      : 'Available portfolio risk/capital was not verified. Re-check total exposure at execution; saving this plan reserves nothing.'}</span>
                  </div>
                </>
              )}
            </>
          )}

          <label className="plan-full"><span>Notes</span>
            <textarea className="ws-input plan-textarea" value={form.notes} onChange={set('notes')} placeholder="Optional context. Facts and unknowns, not hindsight."/>
          </label>

          {planIssues.length > 0 && (
            <div className="ws-banner warning" role="status">
              <strong>Complete the decision before saving.</strong>
              <span>{planIssues[0]}{planIssues.length > 1 ? ` · ${planIssues.length - 1} more item${planIssues.length === 2 ? '' : 's'}` : ''}</span>
            </div>
          )}

          <div className="plan-save-row">
            <button className="ws-button primary" onClick={savePlan} disabled={planIssues.length > 0}>
              <FiSave/> {form.disposition === 'PLANNED' ? 'Freeze Plan' : 'Log Decision'}
            </button>
            {saved && <span>Saved {saved.id} · v{saved.version}</span>}
          </div>
        </section>

        <aside className="ws-panel pad plan-history">
          <div className="section-header">
            <h2 className="section-title">Recent plans</h2>
            <span className="section-meta">{plans.length}</span>
          </div>

          {plans.length ? (
            <div className="plan-history-list">
              {plans.slice(0,12).map(plan => (
                <article className={`plan-history-card ${requestedPlanId === plan.id ? 'selected' : ''}`} key={plan.id}>
                  <div className="plan-history-head">
                    <div>
                      <strong className="ticker">{plan.symbol}</strong>
                      <span>v{plan.version} · {plan.current_state}</span>
                    </div>
                    <em>{new Date(plan.created_at).toLocaleString()}</em>
                  </div>
                  <div className="plan-history-meta">
                    <div><span>Setup</span><strong>{plan.setup_family || plan.disposition}</strong></div>
                    <div><span>Entry</span><strong>{plan.max_entry_price == null ? '—' : n(plan.max_entry_price)}</strong></div>
                    <div><span>Stop</span><strong>{plan.stop_price == null ? '—' : n(plan.stop_price)}</strong></div>
                    <div><span>Qty</span><strong>{plan.sizing_snapshot?.final_quantity ?? '—'}</strong></div>
                  </div>
                  {(plan.decision_reason || plan.structural_invalidation) && (
                    <p>{plan.decision_reason || plan.structural_invalidation}</p>
                  )}
                  {plan.disposition === 'PLANNED' && !plan.sizing_snapshot?.portfolio_capacity_verified && (
                    <div className="plan-capacity-warning">Capacity unverified · frozen quantity is a single-plan estimate · no reservation created</div>
                  )}
                  {plan.disposition === 'PLANNED' && (
                    <div className="plan-history-actions">
                      <button className="terminal-link" onClick={()=>copyExport(plan.id)}>
                        <FiCopy/> Copy external execution plan
                      </button>
                      {isLocalExecution && plan.current_state === 'PLANNED' && (
                        <button
                          className="ws-button primary"
                          onClick={()=>executePaper(plan)}
                          disabled={executingPlanId === plan.id}
                        >
                          {executingPlanId === plan.id ? 'Submitting…' : 'Submit to IBKR Paper'}
                        </button>
                      )}
                      {['PLANNED','STALE'].includes(plan.current_state) && (
                        <button className="ws-button" onClick={()=>openExternalClose(plan)}>
                          {externalPlanId === plan.id ? 'Cancel external record' : 'Record external close'}
                        </button>
                      )}
                      {plan.current_state === 'CLOSED' && (
                        <button
                          className="ws-button"
                          onClick={()=>reviewClosedPlan(plan)}
                          disabled={reviewingPlanId === plan.id}
                        >
                          {reviewingPlanId === plan.id ? 'Saving review…' : 'Review closed trade'}
                        </button>
                      )}
                    </div>
                  )}
                  {externalPlanId === plan.id && (
                    <div className="external-close-form">
                      <div className="external-close-title">External broker close · simple one-entry / one-exit</div>
                      <div className="external-close-grid">
                        <label><span>Entry price</span><input className="ws-input" inputMode="decimal" value={externalForm.entryPrice} onChange={setExternal('entryPrice')}/></label>
                        <label><span>Exit price</span><input className="ws-input" inputMode="decimal" value={externalForm.exitPrice} onChange={setExternal('exitPrice')}/></label>
                        <label><span>Actual qty</span><input className="ws-input" inputMode="numeric" value={externalForm.quantity} onChange={setExternal('quantity')}/></label>
                        <label><span>Reported initial stop</span><input className="ws-input" inputMode="decimal" value={externalForm.protectiveStop} onChange={setExternal('protectiveStop')}/></label>
                        <label><span>Entry fee</span><input className="ws-input" inputMode="decimal" value={externalForm.entryFee} onChange={setExternal('entryFee')} placeholder="Blank = unknown"/></label>
                        <label><span>Exit fee</span><input className="ws-input" inputMode="decimal" value={externalForm.exitFee} onChange={setExternal('exitFee')} placeholder="Blank = unknown"/></label>
                        <label><span>Entry time</span><input className="ws-input" type="datetime-local" value={externalForm.entryTime} onChange={setExternal('entryTime')}/></label>
                        <label><span>Exit time</span><input className="ws-input" type="datetime-local" value={externalForm.exitTime} onChange={setExternal('exitTime')}/></label>
                        <label className="external-span"><span>Broker / source</span><input className="ws-input" value={externalForm.brokerLabel} onChange={setExternal('brokerLabel')} placeholder={isLocalExecution ? 'Saxo, IBKR, manual...' : 'Optional trade source'}/></label>
                        <label className="external-span"><span>Execution notes</span><textarea className="ws-input plan-textarea" value={externalForm.notes} onChange={setExternal('notes')} placeholder="Optional deviations or execution context."/></label>
                      </div>
                      <div className="plan-actions">
                        <button className="ws-button primary" disabled={externalSaving} onClick={()=>submitExternalClose(plan)}>{externalSaving ? 'Recording…' : 'Record closed trade'}</button>
                        <span>Blank fee = unknown. AlgoStock will not invent net P/L or R.</span>
                      </div>
                    </div>
                  )}
                </article>
              ))}
            </div>
          ) : (
            <div className="empty-state"><strong>No plans yet</strong>Freeze a plan or log a no-trade decision.</div>
          )}
        </aside>
      </div>
    </div>
  );
};

export default Plans;
